import React, { useContext } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { NotificationContext } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';

// React Icons
import {
  FiHome,
  FiSearch,
  FiFileText,
  FiPlusCircle,
  FiUser,
  FiSettings,
  FiBell,
  FiShield,
  FiCalendar,
  FiStar,
} from 'react-icons/fi';

const BottomNav = () => {
  const { currentUser, role } = useContext(AuthContext);
  const { unreadCount, toggleModal } = useContext(NotificationContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [showCreateReportPopover, setShowCreateReportPopover] = React.useState(false);

  // Enhanced backdrop click handling for mobile
  React.useEffect(() => {
    const handleBackdropClick = (e) => {
      if (showCreateReportPopover) {
        // Check if click is outside the popover content
        const popoverElement = document.querySelector('[data-popover-content="true"]');
        if (popoverElement && !popoverElement.contains(e.target)) {
          setShowCreateReportPopover(false);
        }
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape' && showCreateReportPopover) {
        setShowCreateReportPopover(false);
      }
    };

    if (showCreateReportPopover) {
      // Add both click and touch listeners for mobile compatibility
      document.addEventListener('click', handleBackdropClick, true);
      document.addEventListener('touchend', handleBackdropClick, true);
      document.addEventListener('keydown', handleEscape);
      
      // Prevent body scroll when popover is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('click', handleBackdropClick, true);
      document.removeEventListener('touchend', handleBackdropClick, true);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showCreateReportPopover]);

  const reportTypesForPopover = [
    { path: '/create-dor', Icon: FiFileText, label: 'Daily Observation', description: 'Daily performance tracking' },
    { path: '/create-officer-safety-report', Icon: FiShield, label: 'Officer Safety', description: 'Safety incident documentation' },
    { path: '/create-end-of-week-report', Icon: FiCalendar, label: 'End of Week', description: 'Weekly progress summary' },
  ];

  if (!currentUser) return null;

  const navItemsConfig = [
    { to: '/', Icon: FiHome, label: 'Home', ariaLabel: 'Navigate to Home' },
    { to: '/search-reports', Icon: FiSearch, label: 'Search', ariaLabel: 'Search Reports' },
    ...(role === 'trainer' || role === 'admin'
      ? [
          { to: '/dors', Icon: FiFileText, label: 'DORs', ariaLabel: 'View Daily Observation Reports' },
          {
            id: 'new-report',
            Icon: FiPlusCircle,
            label: 'New',
            ariaLabel: 'Create New Report',
          }
        ]
      : []
    ),
    { to: '/profile', Icon: FiUser, label: 'Profile', ariaLabel: 'View User Profile' },
    ...(role === 'admin' ? [{ to: '/settings', Icon: FiSettings, label: 'Settings', ariaLabel: 'Open Settings (Admin Only)' }] : [])
  ];

  // Enhanced navigation item style
  const navItemClasses = `
    flex flex-col items-center justify-center py-3 px-2 rounded-xl mx-1 my-0.5
    transition-all duration-300 ease-out relative group
    hover:bg-gradient-to-r hover:from-accent-light/20 hover:to-accent-light/10 
    dark:hover:from-accent-dark/20 dark:hover:to-accent-dark/10
    hover:text-accent-light dark:hover:text-accent-dark hover:shadow-lg hover:scale-105
    backdrop-blur-sm overflow-hidden
    focus:outline-none focus:ring-2 focus:ring-accent-light/50 dark:focus:ring-accent-dark/50 focus:ring-offset-2
    active:scale-95 active:transition-transform active:duration-75
  `;

  const activeNavItemClasses = `
    text-accent-light dark:text-accent-dark bg-gradient-to-r from-accent-light/30 to-accent-light/20
    dark:from-accent-dark/30 dark:to-accent-dark/20 shadow-lg scale-105
    border border-accent-light/30 dark:border-accent-dark/30
  `;

  const renderNavItem = (item) => {
    const isActive = location.pathname === item.to;
    
    if (item.id === 'new-report') {
      return (
        <div key={item.id} className="relative">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCreateReportPopover(!showCreateReportPopover)}
            className={`${navItemClasses} ${showCreateReportPopover ? activeNavItemClasses : 'text-text-light dark:text-text-dark'}`}
            aria-label={item.ariaLabel}
            aria-expanded={showCreateReportPopover}
          >
            <div className="relative">
              <item.Icon className="flex-shrink-0 transition-all duration-300 group-hover:scale-125 group-hover:rotate-12" size={20} />
              {/* Subtle activity indicator */}
              <motion.div
                className="absolute -bottom-1 -right-1 w-2 h-2 bg-green-400 rounded-full opacity-0 group-hover:opacity-100
                           transition-opacity duration-300 shadow-sm"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
            <span className="text-xs mt-1 font-medium">{item.label}</span>
          </motion.button>

          {/* Create Report Popover */}
          <AnimatePresence>
            {showCreateReportPopover && (
              <>
                {/* Enhanced Backdrop with maximum z-index */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 backdrop-blur-md"
                  style={{ 
                    zIndex: 999998,
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    position: 'fixed'
                  }}
                />
                
                {/* Popover Content */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  data-popover-content="true"
                  className="fixed bg-primary-light dark:bg-primary-dark backdrop-blur-xl 
                             border border-secondary-light/50 dark:border-secondary-dark/50 rounded-2xl 
                             shadow-2xl shadow-gray-900/20 dark:shadow-black/40 py-3 overflow-hidden
                             w-72"
                  role="menu"
                  style={{
                    zIndex: 999999,
                    bottom: 'calc(env(safe-area-inset-bottom) + 4.5rem)',
                    left: '1rem',
                    maxWidth: 'calc(100vw - 2rem)',
                    minWidth: '260px',
                    touchAction: 'manipulation'
                  }}
                >
                  {/* Drop-up arrow indicator positioned towards the New button */}
                  <div className="absolute -bottom-2 z-10"
                       style={{ right: '2rem' }}>
                    <div className="w-4 h-4 bg-primary-light dark:bg-primary-dark rotate-45 border-r border-b 
                                    border-secondary-light/50 dark:border-secondary-dark/50"></div>
                  </div>
                  
                  {/* Popover header */}
                  <div className="px-4 pb-2 border-b border-secondary-light/30 dark:border-secondary-dark/30">
                    <h3 className="text-sm font-semibold text-text-light dark:text-text-dark flex items-center">
                      <FiPlusCircle className="w-4 h-4 mr-2 text-accent-light dark:text-accent-dark" />
                      Create New Report
                    </h3>
                    <p className="text-xs text-text-light/70 dark:text-text-dark/70 mt-1">Choose a report type to create</p>
                  </div>
                  
                  {/* Report options */}
                  <div className="py-2">
                    {reportTypesForPopover.map((report, index) => (
                      <motion.button
                        key={report.path}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.2 }}
                        onClick={() => { 
                          navigate(report.path); 
                          setShowCreateReportPopover(false); 
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-text-light dark:text-text-dark 
                                   hover:bg-gradient-to-r hover:from-accent-light/20 hover:to-accent-light/10
                                   dark:hover:from-accent-dark/20 dark:hover:to-accent-dark/10
                                   hover:text-accent-light dark:hover:text-accent-dark
                                   flex items-center group transition-all duration-200 mx-2 rounded-xl"
                        role="menuitem"
                      >
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg 
                                        bg-secondary-light dark:bg-secondary-dark group-hover:bg-accent-light/20 
                                        dark:group-hover:bg-accent-dark/20 mr-3 transition-all duration-200 flex-shrink-0">
                          <report.Icon className="w-5 h-5 text-text-light dark:text-text-dark 
                                                 group-hover:text-accent-light dark:group-hover:text-accent-dark 
                                                 transition-colors duration-200" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{report.label}</div>
                          <div className="text-xs text-text-light/70 dark:text-text-dark/70 mt-0.5 line-clamp-2">
                            {report.description}
                          </div>
                        </div>
                        <motion.div
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0 ml-2"
                          whileHover={{ x: 3 }}
                        >
                          <svg className="w-4 h-4 text-accent-light dark:text-accent-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </motion.div>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      );
    }

    return (
      <NavLink
        key={item.to}
        to={item.to}
        className={({ isActive }) => `${navItemClasses} ${isActive ? activeNavItemClasses : 'text-text-light dark:text-text-dark'}`}
        aria-label={item.ariaLabel}
      >
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex flex-col items-center"
        >
          <div className="relative">
            <item.Icon className="flex-shrink-0 transition-all duration-300 group-hover:scale-125 group-hover:rotate-12" size={20} />
            {/* Admin indicator for Settings */}
            {item.to === '/settings' && (
              <div className="absolute -top-1 -right-1 inline-flex items-center justify-center
                              rounded-full text-[7px] font-bold bg-gradient-to-r from-yellow-400 to-yellow-500 
                              text-yellow-900 min-w-[10px] h-[10px] leading-none shadow-lg
                              border border-yellow-300">
                <FiStar size={8} />
              </div>
            )}
            {/* Subtle activity indicator */}
            <motion.div
              className="absolute -bottom-1 -right-1 w-2 h-2 bg-green-400 rounded-full opacity-0 group-hover:opacity-100
                         transition-opacity duration-300 shadow-sm"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
          <span className="text-xs mt-1 font-medium">{item.label}</span>
        </motion.div>
      </NavLink>
    );
  };

  return (
    <>
      {/* Modern Bottom Navigation with Glassmorphism */}
      <motion.nav
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="
          fixed bottom-0 left-0 right-0 z-50 md:hidden
          bg-gradient-to-t from-primary-light/98 via-primary-light/96 to-primary-light/90 
          dark:from-primary-dark/95 dark:to-secondary-dark/95
          backdrop-blur-xl border-t-2 border-secondary-light/80 dark:border-secondary-dark/50
          shadow-2xl shadow-gray-900/20 dark:shadow-black/30
          transition-all duration-300
        "
        style={{
          paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))',
          touchAction: 'manipulation',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
        }}
      >
        <div className="flex justify-between items-center px-2 py-2">
          {navItemsConfig.map((item) => renderNavItem(item))}

          {/* Enhanced Alerts Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleModal}
            className={`${navItemClasses} text-text-light dark:text-text-dark`}
            aria-label="View Notifications"
          >
            <div className="relative">
              <FiBell className="flex-shrink-0 transition-all duration-300 group-hover:scale-125 group-hover:rotate-12" size={20} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center p-0.5 px-1 
                               rounded-full text-[8px] font-bold bg-gradient-to-r from-red-500 to-red-600 
                               text-white min-w-[16px] h-[16px] leading-none shadow-lg
                               border-2 border-primary-light dark:border-primary-dark">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
              {/* Subtle activity indicator */}
              <motion.div
                className="absolute -bottom-1 -right-1 w-2 h-2 bg-green-400 rounded-full opacity-0 group-hover:opacity-100
                           transition-opacity duration-300 shadow-sm"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
            <span className="text-xs mt-1 font-medium">Alerts</span>
          </motion.button>
        </div>
      </motion.nav>
    </>
  );
};

export default BottomNav;